/**
 * API の失敗。HTTP の状態（401/403/409/422/503）と、サーバーが返す分類コードを持つ。
 * 分類コードの一覧はまだ契約に無い（docs/API_NEEDS.md で依頼中）。ここの文言は仮のコードに対するもので、
 * 知らないコードが来たときは HTTP の状態から一般的な説明を出す。
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  /** ★ 補足（例：同じ区間の既存登録の ID）。契約にまだ無い */
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message?: string, correlationId?: string, details?: Record<string, unknown>) {
    super(message || code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.details = details;
  }
}

/** 契約にまだ無い操作。成功したように見せない */
export class NotConnectedError extends ApiError {
  readonly operation: string;
  constructor(operation: string) {
    super(0, "not_connected", "この操作はまだAPIに接続されていません。");
    this.name = "NotConnectedError";
    this.operation = operation;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/**
 * 操作の失敗・拒否の理由を、利用者向けの文に。
 * コードは契約のバックエンド（backend/src/music_community/errors.py・worker.py、2026-10-13）が返すもの。
 * 知らないコードは、サーバーの文（ErrorDetail.message）があればそれを、無ければ HTTP の状態から一般的な説明を出す
 */
const CODE_TEXT: Record<string, string> = {
  mix_save_stale: "曲またはMixの状態が変わりました。いま表示されている曲を確認して、もう一度保存してください。",
  mix_save_unavailable: "この曲の情報をまだ確認できないため、保存できません。少し待ってからお試しください。",
  mix_preference_stale: "希望の状態が変わりました。最新の内容を確認して、もう一度送ってください。",
  mix_preference_unavailable: "DJ Mixをオンにしてから希望を伝えてください。",
  mix_preference_reference_missing: "選んだ歌い手または参考曲が見つかりません。選び直してください。",
  // 接続・Bot
  network: "通信できませんでした。電波の良いところで、もう一度お試しください。",
  not_connected: "この操作はまだAPIに接続されていません。",
  worker_unavailable: "Botが停止中か、Discordへ再接続中です。再生の操作はできません（閲覧と保存はできます）。",
  worker_not_initialized: "Botの初期準備が終わっていません。少し待ってからお試しください。",
  gateway_syncing: "BotがDiscordと状態を合わせています。終わるまで操作を受け付けません。",
  discord_unavailable: "Discordの所属情報を確認できません。時間をおいてお試しください。",
  database_unavailable: "保存先へ接続できません。時間をおいてお試しください。",
  too_many_pending_commands: "実行待ちの操作が多すぎます。少し待ってからお試しください。",
  command_expired: "受け付けてから時間がたったため、実行しませんでした。",
  worker_restarted: "Botが再起動したため、中断しました。自動ではやり直しません。",
  worker_stopped: "Botが止まったため、中断しました。自動ではやり直しません。",
  execution_interrupted: "実行の途中で止まったため、中断しました。自動ではやり直しません。",
  voice_disconnected: "VCから切断されたため、中断しました。",
  voice_permission_missing: "BotにこのVCへの接続・発言の権限がありません。サーバーの管理者に確認してください。",
  voice_start_failed: "VCへの接続を始められませんでした。",
  unsupported_voice_channel: "このチャンネルでは再生できません（ステージチャンネルは対象外です）。",
  playback_lock_lost: "再生の担当が切りかわったため、止めました。",
  audio_failed: "音声の送信に失敗しました。",
  // ログイン・VC・権限
  unauthenticated: "ログインが切れました。もう一度ログインしてください。",
  membership_required: "対象のDiscordサーバーのメンバーだけが使えます。",
  wrong_guild: "対象のDiscordサーバーのメンバーだけが使えます。",
  csrf_failed: "画面を読み込み直してから、もう一度お試しください（確認用の情報が古くなっています）。",
  forbidden: "この操作をする権限がありません。",
  dj_required: "DJ・管理者だけができる操作です。",
  playback_owner_required: "DJ Mixの設定・一時停止・再開は、現在の再生オーナーだけが操作できます。",
  mix_operation_unavailable: "DJ Mixの再生中は、この操作を使えません。手動で曲を追加するにはMixをオフにしてください。",
  mix_control_stale_target: "DJ Mixの状態が変わっていたため、実行しませんでした。最新の状態を見てから、もう一度操作してください。",
  mix_control_usage_changed: "再生の利用が切り替わったため、実行しませんでした。画面を更新してください。",
  playback_ending: "再生を終了しています。接続が閉じるまで少し待ってください。",
  same_voice_channel_required: "Botと同じVCに参加してください。",
  different_voice_channel: "Botは別のVCで再生中です。そのVCに参加すると操作できます。",
  not_in_voice: "再生の操作には、VCへの参加が必要です。",
  // 競合
  stale_generation: "曲または再生の状態が変わっていたため、実行しませんでした。最新の状態を見てから、もう一度操作してください。",
  stale_revision: "キュー（一覧）が先に変わっていたため、実行しませんでした。最新の状態を見てから、もう一度操作してください。",
  idempotency_conflict: "同じ操作IDが別の内容に使われていたため、受け付けませんでした。",
  conflict: "ほかの操作と重なったため、実行しませんでした。最新の状態にしました。",
  // 再生
  queue_empty: "再生待ちの曲がありません。曲を追加すると再生できます。",
  queue_full: "キューがいっぱいです。",
  not_queued: "その曲はもうキューにありません。",
  not_playing: "いまは再生中の曲がありません。",
  not_seekable: "いまは位置を移動できません。",
  seek_out_of_bounds: "区間の外へは移動できません。",
  invalid_queue_order: "並べ替えの内容が、いまのキューと合いません。最新の状態にしてから操作してください。",
  already_active: "すでに再生しています。",
  // ここ好き
  not_capturable: "再生中・一時停止中の曲だけ記録できます（読み込み中は記録できません）。",
  position_unavailable: "再生位置を確認できないため、記録できませんでした。",
  playback_ended: "その曲の再生は終わっていたため、記録しませんでした（別の曲には付けていません）。",
  moment_out_of_bounds: "ここ好きの時刻が、歌唱の区間の外です。",
  // 動画・登録
  source_unavailable: "元動画を取得できません（削除・非公開・地域制限・ログインが必要など）。",
  audio_node_unavailable: "音声サーバーへ接続できません。再接続後に再生してください。",
  source_access_blocked: "YouTubeが音源の取得を拒否しました。取得要求を約1分控えています。時間を置いて再試行してください。",
  source_timeout: "音源の読み込みが時間切れになりました。曲は残しています。再生を押すともう一度試せます。",
  source_rate_limited: "YouTubeへの要求が制限されています。時間を置いてから再生を試してください。",
  youtube_auth_invalid: "Bot専用のYouTube認証設定を読み込めません。管理者による設定確認が必要です。",
  video_unavailable: "元動画を利用できません（削除・非公開・地域制限・ログインが必要など）。",
  unknown_video_duration: "動画の長さを確認できないため、登録を確定できません。",
  unsupported_video: "配信中・配信予定・処理中など、登録できない動画です。公開済みの動画・アーカイブを指定してください。",
  invalid_youtube_url: "公開されているYouTube動画のURL（https://…）を入れてください。",
  invalid_video_id: "動画のIDを読み取れませんでした。URLを確認してください。",
  unsafe_media_source: "この動画は扱えません。",
  segment_out_of_bounds: "歌唱の区間が、動画の長さを超えています。",
  duplicate_segment: "同じ動画・同じ区間の歌唱がすでに登録されています。",
  invalid_introduction: "紹介の内容を確認してください。",
  validation_error: "入力内容を確認してください。",
  data_conflict: "ほかの操作と重なりました。表示を新しくしてから、もう一度お試しください。",
  internal_error: "サーバーでエラーが起きました。時間をおいて、もう一度お試しください。",
  // ログインの途中（/auth のページ移動で起きる。API の応答で来たときのため）
  auth_not_configured: "ログインの設定がまだ済んでいません。管理者に知らせてください。",
  invalid_oauth_state: "ログインの手続きが古くなりました。もう一度ログインしてください。",
  missing_oauth_scope: "Discordで必要な許可が得られませんでした。もう一度ログインして、許可してください。",
  invalid_discord_response: "Discordからの応答を確認できませんでした。時間をおいて、もう一度お試しください。",
  // ワーカーの内部（再生の状態に出たとき）
  worker_already_running: "Botの再生処理がすでに動いています。",
  lock_lost: "Botが再生の担当を失ったため、止まりました。キューは残っています。",
  not_found: "見つかりませんでした（削除されたか、見る権限がありません）。",
};

const STATUS_TEXT: Record<number, string> = {
  401: CODE_TEXT.unauthenticated,
  403: CODE_TEXT.forbidden,
  404: CODE_TEXT.not_found,
  409: CODE_TEXT.conflict,
  422: CODE_TEXT.validation_error,
  429: "操作が続いたため、少し待ってからお試しください。",
  503: "サーバーかBotが利用できません。しばらくしてからお試しください。",
};

export function describeCode(code: string | null | undefined, status?: number, serverMessage?: string): string {
  if (code && CODE_TEXT[code]) return CODE_TEXT[code];
  if (serverMessage && serverMessage !== code) return serverMessage;
  if (status && STATUS_TEXT[status]) return STATUS_TEXT[status];
  return "うまくいきませんでした。時間をおいて、もう一度お試しください。";
}

export function describeError(e: unknown): string {
  if (isApiError(e)) return describeCode(e.code, e.status, e.message);
  return describeCode("network");
}

/** 動画が使えない理由 */
export const UNAVAILABLE_TEXT: Record<string, string> = {
  deleted: "元動画が削除されています",
  private: "元動画が非公開になっています",
  region: "地域制限で再生できません",
  login_required: "ログインが必要な動画のため再生できません",
  unknown: "元動画を利用できません",
};
